import { apiFetch, apiFetchJson } from "@/utils/apiFetch";

const BASE_API_URL = import.meta.env.VITE_API_URL;

export type NoteMention = {
  id: string;
  name: string;
  email: string;
};

export type ApiNote = {
  id: number;
  entity_key: string;
  author_name: string;
  author_role: string;
  trial_tag: string | null;
  text: string;
  mentions: NoteMention[];
  created_at: string;
  edited_at: string | null;
};

/** A trial member who can be @mentioned. */
export type MentionableUser = {
  id: string;
  name: string;
  email: string;
  role: string;
};

type NotesResponse = {
  count: number;
  results: ApiNote[];
};

type MentionableUsersResponse = {
  count: number;
  results: MentionableUser[];
};

function notesUrl(scope: string, id: string, entityKey: string): string {
  return `${BASE_API_URL}/data/${scope}/${encodeURIComponent(
    id,
  )}/notes/${encodeURIComponent(entityKey)}/`;
}

export const notesService = {
  listNotes: async (
    scope: string,
    id: string,
    entityKey: string,
  ): Promise<ApiNote[]> => {
    const data = await apiFetchJson<NotesResponse>(
      notesUrl(scope, id, entityKey),
    );
    return data.results ?? [];
  },

  createNote: async (
    scope: string,
    id: string,
    entityKey: string,
    payload: {
      text: string;
      trial_tag?: string | null;
      mentions?: NoteMention[];
    },
  ): Promise<ApiNote> => {
    return apiFetchJson<ApiNote>(notesUrl(scope, id, entityKey), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateNote: async (
    scope: string,
    id: string,
    entityKey: string,
    noteId: number,
    payload: { text: string; mentions?: NoteMention[] },
  ): Promise<ApiNote> => {
    return apiFetchJson<ApiNote>(
      `${notesUrl(scope, id, entityKey)}${noteId}/`,
      { method: "PATCH", body: JSON.stringify(payload) },
    );
  },

  listMentionableUsers: async (
    scope: string,
    id: string,
  ): Promise<MentionableUser[]> => {
    const data = await apiFetchJson<MentionableUsersResponse>(
      `${BASE_API_URL}/data/${scope}/${encodeURIComponent(
        id,
      )}/notes/mentionable-users/`,
    );
    return data.results ?? [];
  },

  deleteNote: async (
    scope: string,
    id: string,
    entityKey: string,
    noteId: number,
  ): Promise<void> => {
    await apiFetch(`${notesUrl(scope, id, entityKey)}${noteId}/`, {
      method: "DELETE",
    });
  },
};
